import React from 'react';
import { KeyRound, Lock, Settings as SettingsIcon } from 'lucide-react';
import Ghost from '../auth/Ghost.jsx';

/**
 * The AI panel's first-run / not-ready state, shown instead of the chat empty
 * state whenever a turn cannot run (services/ai/engine.js checkAiReadiness).
 *
 * This is the migration UX for every existing user after the backend AI
 * proxy retired (16.08.2026): their first panel open lands here, so the copy
 * explains WHY in one sentence and offers exactly one primary action.
 *
 *   no_key       — key missing: one button to Settings → AI Chat.
 *   vault_locked — the key may exist but the vault can't answer: one button
 *                  that opens the vault gate (which itself shows unlock OR
 *                  first-time setup, whichever applies).
 *   no_vault     — vault unreachable (browser dev mode): explain, no button.
 */
function AiKeySetupState({ state, onOpenSettings, onUnlockVault }) {
  if (state === 'vault_locked') {
    return (
      <div className="ai-chat-empty-state flex flex-col items-center text-center gap-4" data-testid="ai-key-setup-state">
        <Ghost variant="classic" size={48} animated glow />
        <div className="space-y-2 max-w-[16rem]">
          <p className="text-sm font-medium text-gray-600 dark:text-zinc-300">Unlock your vault</p>
          <p className="text-xs leading-relaxed text-gray-400 dark:text-zinc-500">
            Your AI key is stored in your encrypted vault. Unlock it to start chatting.
          </p>
        </div>
        <button
          type="button"
          onClick={onUnlockVault}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer"
        >
          <Lock size={13} />
          Unlock vault
        </button>
      </div>
    );
  }

  if (state === 'no_vault') {
    return (
      <div className="ai-chat-empty-state flex flex-col items-center text-center gap-4" data-testid="ai-key-setup-state">
        <Ghost variant="classic" size={48} animated glow />
        <div className="space-y-2 max-w-[16rem]">
          <p className="text-sm font-medium text-gray-600 dark:text-zinc-300">Vault unavailable</p>
          <p className="text-xs leading-relaxed text-gray-400 dark:text-zinc-500">
            Your AI keys are stored in your encrypted vault, which needs the
            Apiboo desktop app. Open the desktop app to use the AI assistant.
          </p>
        </div>
      </div>
    );
  }

  // no_key — the standard first-run state.
  return (
    <div className="ai-chat-empty-state flex flex-col items-center text-center gap-4" data-testid="ai-key-setup-state">
      <Ghost variant="classic" size={48} animated glow />
      <div className="space-y-2 max-w-[16rem]">
        <p className="text-sm font-medium text-gray-600 dark:text-zinc-300">Bring your own AI key</p>
        <p className="text-xs leading-relaxed text-gray-400 dark:text-zinc-500">
          The assistant now runs on your own API key (Anthropic, OpenAI, or
          Gemini) — your usage, your account, stored encrypted in your vault.
        </p>
      </div>
      <button
        type="button"
        onClick={onOpenSettings}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer"
      >
        <KeyRound size={13} />
        Set up in Settings
      </button>
      <p className="text-[11px] leading-relaxed text-gray-400 dark:text-zinc-500 inline-flex items-center gap-2">
        <SettingsIcon size={11} className="flex-shrink-0" />
        Settings → AI Chat
      </p>
    </div>
  );
}

export default AiKeySetupState;
